// SCHEDULE_MODEL_PHASE10_20260911
// TEACHING_SCHEDULE_ASSIGNMENT_FIXES_20260916
import { addDaysToDateString } from './assignmentTiming';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders(extra = {}) {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(response, label) {
  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : null;
}

function inFilter(values) {
  return [...new Set((values || []).filter(Boolean).map(String))].join(',');
}

export async function loadStudentAssignmentScheduleContext({
  courseIds,
  enrolments,
  includeChoiceState = true
}) {
  const ids = [...new Set((courseIds || []).filter(Boolean).map(String))];

  if (ids.length === 0) {
    return {
      scheduleItems: [],
      scheduleReadings: [],
      scheduleEvents: [],
      duplicateLinks: [],
      choiceSelections: [],
      occupancy: []
    };
  }

  const courseFilter = inFilter(ids);

  const [
    itemResponse,
    readingResponse,
    eventResponse,
    duplicateResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_schedule_items?select=id,course_assignment_id,item_type,week_reading_assignment_id,course_event_id,display_order,capacity,configuration_mode,duplicate_source_schedule_item_id,course_assignments!inner(course_id)&course_assignments.course_id=in.(${courseFilter})&order=display_order.asc,created_at.asc`,
      { headers: apiHeaders() }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?select=id,course_week_id,display_order,course_readings(id,title,author,drive_file_id),course_weeks!inner(id,course_id,week_number,class_date)&course_weeks.course_id=in.(${courseFilter})&order=display_order.asc`,
      { headers: apiHeaders() }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_events?select=id,course_week_id,title,event_date,event_time,location,course_weeks!inner(id,course_id,week_number,class_date)&course_weeks.course_id=in.(${courseFilter})`,
      { headers: apiHeaders() }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_schedule_duplicate_details?course_id=in.(${courseFilter})&select=*`,
      { headers: apiHeaders() }
    )
  ]);

  const [
    scheduleItems,
    scheduleReadings,
    scheduleEvents,
    duplicateLinks
  ] = await Promise.all([
    readJson(itemResponse, 'Loading assignment Schedule links'),
    readJson(readingResponse, 'Loading scheduled readings'),
    readJson(eventResponse, 'Loading course events'),
    readJson(duplicateResponse, 'Loading duplicated assignment Schedules')
  ]);

  const context = {
    scheduleItems: Array.isArray(scheduleItems) ? scheduleItems : [],
    scheduleReadings: Array.isArray(scheduleReadings) ? scheduleReadings : [],
    scheduleEvents: Array.isArray(scheduleEvents) ? scheduleEvents : [],
    duplicateLinks: Array.isArray(duplicateLinks) ? duplicateLinks : [],
    choiceSelections: [],
    occupancy: []
  };

  if (!includeChoiceState) {
    return context;
  }

  const enrolmentRows = (enrolments || []).filter(
    enrolment => enrolment?.id && (
      enrolment?.courses?.id || enrolment?.course_id
    )
  );

  const choiceStates = await Promise.all(
    enrolmentRows.map(async enrolment => {
      const courseId = enrolment?.courses?.id || enrolment?.course_id;

      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/rpc/student_get_assignment_schedule_choice_state`,
        {
          method: 'POST',
          headers: apiHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            p_enrolment_id: enrolment.id,
            p_course_id: courseId
          })
        }
      );

      const state = await readJson(
        response,
        'Loading your assignment Schedule choices'
      );

      return {
        enrolmentId: enrolment.id,
        courseId,
        selections: Array.isArray(state?.selections) ? state.selections : [],
        occupancy: Array.isArray(state?.occupancy) ? state.occupancy : []
      };
    })
  );

  const occupancyByItem = new Map();

  choiceStates.forEach(state => {
    state.selections.forEach(selection => {
      context.choiceSelections.push({
        ...selection,
        context_enrolment_id: state.enrolmentId,
        context_course_id: state.courseId
      });
    });

    state.occupancy.forEach(row => {
      const key = String(row.course_assignment_schedule_item_id || '');
      if (!key) return;

      occupancyByItem.set(key, {
        course_assignment_schedule_item_id:
          row.course_assignment_schedule_item_id,
        count: Number(row.count) || 0
      });
    });
  });

  context.occupancy = [...occupancyByItem.values()];

  return context;
}

export function expandInstructorAssignmentScheduleOccurrences({
  assignments,
  assignmentTypeSettings,
  scheduleContext,
  scheduleSelections = []
}) {
  const settingsByCourseAndType =
    new Map(
      (assignmentTypeSettings || [])
        .map(setting => [
          String(setting.course_id) +
            '|' +
            String(setting.assignment_type_id),

          setting
        ])
    );

  const result = [];


  (assignments || []).forEach(
    assignment => {
      const requiresSubmission =
        assignment
          ?.assignment_types
          ?.requires_submission ??
        true;


      const setting =
        settingsByCourseAndType.get(
          String(
            assignment.course_id
          ) +
          '|' +
          String(
            assignment.assignment_type_id
          )
        );


      const scheduleEnabled =
        requiresSubmission &&
        setting?.schedule_enabled === true;


      if (!scheduleEnabled) {
        result.push(
          assignment
        );

        return;
      }


      const duplicateInfo =
        getEffectiveAssignmentScheduleDuplicateInfo(
          scheduleContext,
          assignment.id
        );


      const choiceEnabled =
        duplicateInfo
          ? duplicateInfo
              .effectiveChoiceEnabled ===
            true

          : setting
              ?.schedule_choice_enabled ===
            true;


      const activeMode =
        choiceEnabled
          ? 'choice'
          : 'single';


      const linkedItems =
        (
          scheduleContext
            ?.scheduleItems ||
          []
        )
          .filter(
            item =>
              String(
                item
                  .course_assignment_id
              ) ===
                String(
                  assignment.id
                ) &&

              item
                .configuration_mode ===
                activeMode
          )
          .sort(
            (a, b) =>
              (
                Number(
                  a.display_order
                ) || 0
              ) -
              (
                Number(
                  b.display_order
                ) || 0
              )
          );


      /*
       * IMPORTANT:
       *
       * Choice options are merely possibilities.
       * They are not instructor-visible scheduled
       * occurrences until somebody has actually
       * selected them.
       */
      const selectedItemIds =
        choiceEnabled

          ? new Set(
              (
                scheduleSelections ||
                []
              )
                .filter(
                  selection =>
                    String(
                      selection
                        .course_assignment_id
                    ) ===
                    String(
                      assignment.id
                    )
                )
                .map(
                  selection =>
                    String(
                      selection
                        .course_assignment_schedule_item_id
                    )
                )
                .filter(Boolean)
            )

          : null;


      const displayedItems =
        choiceEnabled

          ? linkedItems.filter(
              item =>
                selectedItemIds.has(
                  String(
                    item.id
                  )
                )
            )

          : linkedItems;


      if (
        displayedItems.length ===
        0
      ) {
        /*
         * Keep the old fallback for a
         * normal non-choice scheduled
         * assignment.
         *
         * For choice assignments,
         * however, no selected option
         * means there is no actual
         * occurrence to display yet.
         */
        if (
          !choiceEnabled &&
          (
            assignment.due_date ||
            assignment.occurs_on
          )
        ) {
          result.push(
            assignment
          );
        }

        return;
      }


      displayedItems.forEach(
        item => {
          const selectedGroupNumbers =
            Array.from(
              new Set(
                (
                  scheduleSelections ||
                  []
                )
                  .filter(
                    selection =>
                      String(
                        selection.course_assignment_id
                      ) ===
                        String(
                          assignment.id
                        ) &&
                      String(
                        selection.course_assignment_schedule_item_id
                      ) ===
                        String(
                          item.id
                        ) &&
                      selection.group_number !== null &&
                      selection.group_number !== undefined &&
                      selection.group_number !== ''
                  )
                  .map(
                    selection =>
                      Number(
                        selection.group_number
                      )
                  )
                  .filter(
                    Number.isFinite
                  )
              )
            )
              .sort(
                (a, b) => a - b
              );


          const dates =
            getScheduleItemDates(
              item,
              setting,
              scheduleContext,
              0
            );


          result.push({
            ...assignment,

            schedule_group_numbers:
              selectedGroupNumbers,

            instructor_assignment_occurrence_key:
              String(
                assignment.id
              ) +
              '|' +
              String(
                item.id
              ),

            course_assignment_schedule_item_id:
              item.id,

            schedule_occurrence:
              true,

            schedule_item_type:
              item.item_type,

            schedule_source_title:
              dates.title,

            schedule_source_meta:
              dates.meta,

            schedule_anchor_date:
              dates.anchorDate,

            effective_open_date:
              dates.openDate,

            effective_base_due_date:
              dates.baseDueDate,

            effective_due_date:
              dates.dueDate
          });
        }
      );
    }
  );


  return result;
}


export async function confirmStudentAssignmentScheduleChoices({
  enrolmentId,
  assignmentId,
  scheduleItemIds
}) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/student_confirm_assignment_schedule_choices`,
    {
      method: 'POST',
      headers: apiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        p_enrolment_id: enrolmentId,
        p_course_assignment_id: assignmentId,
        p_schedule_item_ids: scheduleItemIds || []
      })
    }
  );

  return readJson(response, 'Confirming assignment choice');
}

export async function confirmStudentSharedAssignmentScheduleChoices({
  enrolmentId,
  assignmentTypeId,
  choices
}) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/student_confirm_shared_assignment_schedule_choices`,
    {
      method: 'POST',
      headers: apiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        p_enrolment_id: enrolmentId,
        p_assignment_type_id: assignmentTypeId,
        p_choices: choices || []
      })
    }
  );

  return readJson(response, 'Confirming shared assignment choices');
}

export function getAssignmentScheduleDuplicateLink(context, assignmentId) {
  return (context?.duplicateLinks || []).find(
    link => String(link.course_assignment_id) === String(assignmentId)
  ) || null;
}

export function getSharedSchedulePoolItemKey(item, context) {
  if (!item?.id) return null;

  const link = getAssignmentScheduleDuplicateLink(
    context,
    item.course_assignment_id
  );

  if (
    link?.sync_kind === 'shared_instance' &&
    item.duplicate_source_schedule_item_id
  ) {
    return item.duplicate_source_schedule_item_id;
  }

  return item.id;
}

export function getEffectiveAssignmentScheduleDuplicateInfo(
  context,
  assignmentId
) {
  const links = context?.duplicateLinks || [];
  const byFollower = new Map(
    links.map(link => [String(link.course_assignment_id), link])
  );

  const directLink = byFollower.get(String(assignmentId)) || null;
  if (!directLink) return null;

  const seen = new Set([String(assignmentId)]);
  let current = directLink;
  let effectiveSourceAssignmentId = current.source_course_assignment_id;
  let effectiveChoiceEnabled =
    current.source_schedule_choice_enabled === true;

  while (current) {
    const sourceId = String(current.source_course_assignment_id || '');
    if (!sourceId || seen.has(sourceId)) break;
    seen.add(sourceId);

    effectiveSourceAssignmentId = current.source_course_assignment_id;
    effectiveChoiceEnabled =
      current.source_schedule_choice_enabled === true;

    current = byFollower.get(sourceId) || null;
  }

  return {
    directLink,
    effectiveSourceAssignmentId,
    effectiveChoiceEnabled
  };
}

export function isScheduleChoiceEnabledForAssignment({
  assignmentId,
  setting,
  context,
  isMaster = false
}) {
  if (isMaster) return false;

  const duplicateInfo =
    getEffectiveAssignmentScheduleDuplicateInfo(context, assignmentId);

  if (duplicateInfo) {
    return duplicateInfo.effectiveChoiceEnabled === true;
  }

  return setting?.schedule_choice_enabled === true;
}

export async function setStudentAssignmentScheduleSelection({
  enrolmentId,
  scheduleItemId,
  selected
}) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/student_set_assignment_schedule_selection`,
    {
      method: 'POST',
      headers: apiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        p_enrolment_id: enrolmentId,
        p_schedule_item_id: scheduleItemId,
        p_selected: !!selected
      })
    }
  );

  return readJson(response, 'Updating assignment Schedule choice');
}

export function getScheduleItemSource(item, context) {
  if (!item) {
    return {
      type: '',
      typeLabel: '',
      title: 'Schedule item',
      meta: '',
      anchorDate: null
    };
  }

  if (item.item_type === 'reading') {
    const occurrence = (context?.scheduleReadings || []).find(
      row => String(row.id) === String(item.week_reading_assignment_id)
    );

    const week = occurrence?.course_weeks;
    const reading = occurrence?.course_readings;

    return {
      type: 'reading',
      typeLabel: 'Reading',
      title: reading?.title || 'Reading',
      meta: [
        week?.week_number ? `Week ${week.week_number}` : null,
        reading?.author ? `by ${reading.author}` : null
      ].filter(Boolean).join(' · '),
      anchorDate: week?.class_date || null,
      reading: reading || null,
      driveFileId: reading?.drive_file_id || null
    };
  }

  const courseEvent = (context?.scheduleEvents || []).find(
    row => String(row.id) === String(item.course_event_id)
  );

  const week = courseEvent?.course_weeks;

  return {
    type: 'event',
    typeLabel: 'Event',
    title: courseEvent?.title || 'Event',
    meta: [
      week?.week_number ? `Week ${week.week_number}` : null,
      courseEvent?.event_time
        ? String(courseEvent.event_time).slice(0, 5)
        : null,
      courseEvent?.location || null
    ].filter(Boolean).join(' · '),
    // Explicit event date wins. A date-less event follows the week it lives in.
    anchorDate:
      courseEvent?.event_date ||
      week?.class_date ||
      null,
      eventTime: courseEvent?.event_time || null};
}

export function getScheduleItemChronologyValue(item, context) {
  const source = getScheduleItemSource(item, context);
  if (!source?.anchorDate) return null;

  const time =
    source.type === 'event' && source.eventTime
      ? String(source.eventTime).slice(0, 8)
      : '00:00:00';

  const value = new Date(`${source.anchorDate}T${time}`).getTime();
  return Number.isFinite(value) ? value : null;
}

export function getScheduleItemDates(item, setting, context, extensionDays = 0) {
  const source = getScheduleItemSource(item, context);
  const anchorDate = source.anchorDate;

  const openOffset = Number.isInteger(
    Number(setting?.schedule_open_offset_days)
  )
    ? Number(setting.schedule_open_offset_days)
    : null;

  const dueOffset = Number.isInteger(
    Number(setting?.schedule_due_offset_days)
  )
    ? Number(setting.schedule_due_offset_days)
    : null;

  const openDate =
    anchorDate && openOffset !== null
      ? addDaysToDateString(anchorDate, openOffset)
      : null;

  const baseDueDate =
    anchorDate && dueOffset !== null
      ? addDaysToDateString(anchorDate, dueOffset)
      : null;

  const safeExtensionDays = Math.max(0, Number(extensionDays) || 0);

  const dueDate =
    baseDueDate && safeExtensionDays > 0
      ? addDaysToDateString(baseDueDate, safeExtensionDays)
      : baseDueDate;

  return {
    ...source,
    openDate,
    baseDueDate,
    dueDate,
    extensionDays: safeExtensionDays
  };
}

export function getChoiceSelectionForItem({
  context,
  enrolmentId,
  assignmentId,
  scheduleItemId
}) {
  return (context?.choiceSelections || []).find(
    selection =>
      String(selection.context_enrolment_id) === String(enrolmentId) &&
      String(selection.course_assignment_id) === String(assignmentId) &&
      String(selection.course_assignment_schedule_item_id) ===
        String(scheduleItemId)
  ) || null;
}

export function getChoiceSelectionsForAssignment({
  context,
  enrolmentId,
  assignmentId
}) {
  return (context?.choiceSelections || []).filter(
    selection =>
      String(selection.context_enrolment_id) === String(enrolmentId) &&
      String(selection.course_assignment_id) === String(assignmentId)
  );
}

export function getScheduleItemOccupancy(context, scheduleItemId) {
  const row = (context?.occupancy || []).find(
    item =>
      String(item.course_assignment_schedule_item_id) ===
      String(scheduleItemId)
  );

  return Number(row?.count) || 0;
}

export function getChoiceWindowState(setting, today, bypass = false) {
  if (bypass) {
    return {
      editable: false,
      state: 'testing',
      message: 'Testing mode shows every linked item.'
    };
  }

  const openDate = setting?.schedule_choice_open_date || null;
  const closeDate = setting?.schedule_choice_close_date || null;

  if (openDate && today < openDate) {
    return {
      editable: false,
      state: 'not_open',
      message: `Choice opens ${openDate}.`
    };
  }

  if (closeDate && today > closeDate) {
    return {
      editable: false,
      state: 'closed',
      message: `Choice closed ${closeDate}.`
    };
  }

  return {
    editable: true,
    state: 'open',
    message: closeDate
      ? `Choice is open until ${closeDate}.`
      : 'Choice is open.'
  };
}

export function expandStudentAssignmentScheduleOccurrences({
  assignments,
  assignmentTypeSettings,
  enrolments,
  memberships,
  submissions,
  scheduleContext,
  isMaster = false
}) {
  const settingsByCourseAndType = new Map(
    (assignmentTypeSettings || []).map(setting => [
      `${setting.course_id}|${setting.assignment_type_id}`,
      setting
    ])
  );

  const enrolmentByCourse = new Map(
    (enrolments || []).map(enrolment => [
      String(enrolment?.courses?.id || enrolment?.course_id || ''),
      enrolment
    ])
  );

  const membershipByAssignmentAndEnrolment = new Map(
    (memberships || []).map(row => [
      `${row.course_assignment_id}|${row.enrolment_id}`,
      row
    ])
  );

  // Scheduled assignment extensions stay assignment-wide. Existing submission
  // rows are therefore used only as the parent/base extension record here.
  const extensionByAssignmentAndEnrolment = new Map();

  (submissions || []).forEach(row => {
    if (
      row?.course_assignment_schedule_item_id ||
      row?.structured_form_instance_id
    ) return;

    const key =
      `${row.course_assignment_id}|${row.enrolment_id}`;

    const extensionDays =
      Math.max(
        0,
        Number(
          row.extension_days
        ) || 0
      );

    const existing =
      extensionByAssignmentAndEnrolment
        .get(key) || 0;

    extensionByAssignmentAndEnrolment
      .set(
        key,
        Math.max(
          existing,
          extensionDays
        )
      );
  });

  const result = [];

  (assignments || []).forEach(assignment => {
    const requiresSubmission =
      assignment.assignment_types?.requires_submission ?? true;

    const setting = settingsByCourseAndType.get(
      `${assignment.course_id}|${assignment.assignment_type_id}`
    );

    const scheduleEnabled =
      requiresSubmission && setting?.schedule_enabled === true;

    if (!scheduleEnabled) {
      result.push(assignment);
      return;
    }

    const enrolment = enrolmentByCourse.get(String(assignment.course_id));
    const enrolmentId = enrolment?.id || null;

    const usesGroups =
      assignment.groups_enabled === true &&
      Number(assignment.group_count) > 0;

    if (usesGroups && !isMaster && enrolmentId) {
      const membership = membershipByAssignmentAndEnrolment.get(
        `${assignment.id}|${enrolmentId}`
      );

      if (!membership) {
        return;
      }
    }

    const choiceEnabled = isScheduleChoiceEnabledForAssignment({
      assignmentId: assignment.id,
      setting,
      context: scheduleContext,
      isMaster
    });

        const activeMode =
      choiceEnabled
        ? 'choice'
        : 'single';

    const allLinkedItems =
      (scheduleContext?.scheduleItems || [])
        .filter(
          item =>
            String(
              item.course_assignment_id
            ) ===
            String(
              assignment.id
            )
        )
        .sort(
          (a, b) =>
            (Number(a.display_order) || 0) -
            (Number(b.display_order) || 0)
        );

    let linkedItems =
      allLinkedItems.filter(
        item =>
          item.configuration_mode ===
          activeMode
      );

    /*
     * Defensive repair for ordinary scheduled
     * assignments.
     *
     * Choice OFF should mean exactly one "single"
     * schedule anchor. If an older/misclassified
     * record has exactly one linked item but it
     * lacks the expected mode, it is still
     * unambiguous and should be used.
     */
    if (
      !choiceEnabled &&
      linkedItems.length === 0 &&
      allLinkedItems.length === 1
    ) {
      linkedItems =
        allLinkedItems;
    }

    /*
     * Most importantly: an ordinary scheduled
     * assignment must never silently disappear
     * from the student's assignment data merely
     * because its schedule link is damaged.
     *
     * Preserve its ordinary dates as a fallback.
     * Choice assignments remain different:
     * no selected occurrence can legitimately
     * mean no scheduled occurrence yet.
     */
    if (linkedItems.length === 0) {
      if (!choiceEnabled) {
        result.push({
          ...assignment,
          student_assignment_timing_source:
            'schedule_fallback'
        });
      }

      return;
    }

    const selectedItemIds = choiceEnabled
      ? new Set(
          getChoiceSelectionsForAssignment({
            context: scheduleContext,
            enrolmentId,
            assignmentId: assignment.id
          }).map(selection =>
            String(selection.course_assignment_schedule_item_id)
          )
        )
      : null;

    // Preserve meaningful historical occurrence work even if Choice was
    // switched off and later back on. Empty failed-upload placeholders do
    // not count.
    const submissionItemIds = new Set(
      (submissions || [])
        .filter(
          row =>
            enrolmentId &&
            String(row.enrolment_id) === String(enrolmentId) &&
            String(row.course_assignment_id) === String(assignment.id) &&
            row.course_assignment_schedule_item_id &&
            (
              row.submitted ||
              row.drive_file_id ||
              String(row.file_name || '').trim() ||
              String(row.text || '').replace(/<[^>]*>/g, '').trim()
            )
        )
        .map(row => String(row.course_assignment_schedule_item_id))
    );

    const extensionDays = enrolmentId
      ? extensionByAssignmentAndEnrolment.get(
          `${assignment.id}|${enrolmentId}`
        ) || 0
      : 0;

    linkedItems.forEach(item => {
      if (
        choiceEnabled &&
        !selectedItemIds.has(String(item.id)) &&
        !submissionItemIds.has(String(item.id))
      ) {
        return;
      }

      const dates = getScheduleItemDates(
        item,
        setting,
        scheduleContext,
        extensionDays
      );

      result.push({
        ...assignment,
        student_assignment_occurrence_key:
          `${assignment.id}|${item.id}`,
        course_assignment_schedule_item_id: item.id,
        schedule_occurrence: true,
        schedule_item_type: item.item_type,
        schedule_source_title: dates.title,
        schedule_source_meta: dates.meta,
        schedule_anchor_date: dates.anchorDate,
        effective_open_date: dates.openDate,
        effective_base_due_date: dates.baseDueDate,
        effective_due_date: dates.dueDate,
        effective_extension_days: dates.extensionDays,
        student_assignment_timing_resolved: true,
        student_assignment_applicable: true,
        student_assignment_timing_source: 'schedule'
      });
    });
  });

  return result;
}
